import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { buttonVariants, defaultLinkColor } from './utils';

function Button({
    className,
    variant,
    size,
    asChild = false,
    color,
    loading = false,
    disabled,
    children,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
        color?: string;
        loading?: boolean;
    }) {
    const Comp = asChild && !loading ? Slot : 'button';
    const linkColorClass = variant === 'link' ? (color ?? defaultLinkColor) : undefined;

    return (
        <Comp
            data-slot="button"
            data-loading={loading ? true : undefined}
            className={cn(
                buttonVariants({ variant, size }),
                linkColorClass,
                loading && 'relative',
                className,
            )}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading
                ? (
                        <>
                            <span className="invisible inline-flex items-center justify-center gap-2" aria-hidden>
                                {children}
                            </span>
                            <span
                                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                                aria-hidden
                            >
                                <Loader2 className="size-4 shrink-0 animate-spin" />
                            </span>
                            <span className="sr-only">{children}</span>
                        </>
                    )
                : children}
        </Comp>
    );
}

export { Button };
